import { Move, RotateCw, Scaling } from 'lucide-react';
import {
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type KeyboardEvent,
  type PointerEvent,
  type RefObject,
  type SetStateAction,
} from 'react';
import { useTranslation } from 'react-i18next';
import type { PdfThumbnails, PlacedImage } from '@/tools/pdf-engine';
import {
  applyField,
  growBox,
  nudge,
  resizeTo,
  rotateTo,
  type BoxLimits,
  type Field,
  type HandleKind,
} from './place-image-box';
import { PlacePanel } from './PlaceImagePanel';
import { displayedSize, normaliseTurn, toPdfPlacement, type DisplayedBox } from './placement-math';

export interface ChosenImage {
  readonly name: string;
  readonly bytes: ArrayBuffer;
  readonly type: 'png' | 'jpeg';
  readonly url: string;
  readonly naturalWidth: number;
  readonly naturalHeight: number;
}

/** On screen, the image box is never smaller than this on its short side (CSS px)… */
const MIN_SHORT_SIDE_PX = 40;
/** …and starts at least this big, so the three 44 px handles never overlap (uiux review). */
const DEFAULT_SHORT_SIDE_PX = 100;
const MAX_PAGE_CSS_WIDTH = 420;

type Drag =
  | {
      readonly kind: 'move';
      readonly startX: number;
      readonly startY: number;
      readonly box: DisplayedBox;
    }
  | { readonly kind: 'rotate' }
  | { readonly kind: 'resize' };

type SetBox = Dispatch<SetStateAction<DisplayedBox>>;

const ARROWS: Readonly<Record<string, readonly [number, number] | undefined>> = Object.freeze({
  ArrowLeft: [-1, 0],
  ArrowRight: [1, 0],
  ArrowUp: [0, -1],
  ArrowDown: [0, 1],
});

/** The page's on-screen width: as wide as its wrapper allows, up to the maximum. */
function usePageWidth(wrapRef: RefObject<HTMLDivElement | null>) {
  const [cssWidth, setCssWidth] = useState(MAX_PAGE_CSS_WIDTH);
  const [measured, setMeasured] = useState(false);
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const measure = () => {
      if (!wrap.clientWidth) return;
      setCssWidth(Math.min(MAX_PAGE_CSS_WIDTH, wrap.clientWidth));
      setMeasured(true);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(wrap);
    return () => observer.disconnect();
  }, [wrapRef]);
  return { cssWidth, measured };
}

/** Draws the page into the canvas (pdf.js applies the file's own rotation; it gets only ours). */
function usePageRender(
  canvasRef: RefObject<HTMLCanvasElement | null>,
  thumbs: Promise<PdfThumbnails>,
  page: number,
  cssWidth: number,
  extraRotation: number,
) {
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let renderer: PdfThumbnails | null = null;
    void thumbs
      .then((t) => {
        renderer = t;
        return t.render(page, canvas, cssWidth, extraRotation);
      })
      .catch(() => undefined);
    return () => {
      renderer?.cancel(page);
    };
  }, [canvasRef, thumbs, page, cssWidth, extraRotation]);
}

interface Gestures {
  readonly scale: number;
  readonly canvasRef: RefObject<HTMLCanvasElement | null>;
  readonly box: DisplayedBox;
  readonly setBox: SetBox;
  readonly limits: BoxLimits;
}

/** Pointer and keyboard handling of the three handles, and a tap on the page. */
function useBoxGestures({ scale, canvasRef, box, setBox, limits }: Gestures) {
  const drag = useRef<Drag | null>(null);
  const pointToPage = (clientX: number, clientY: number): { x: number; y: number } => {
    const rect = canvasRef.current!.getBoundingClientRect();
    return { x: (clientX - rect.left) / scale, y: (clientY - rect.top) / scale };
  };

  const onDown = (event: PointerEvent<HTMLButtonElement>, kind: HandleKind): void => {
    event.stopPropagation();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    const p = pointToPage(event.clientX, event.clientY);
    drag.current = kind === 'move' ? { kind, startX: p.x, startY: p.y, box } : { kind };
  };

  const onMove = (event: PointerEvent<HTMLButtonElement>): void => {
    const d = drag.current;
    if (!d) return;
    const p = pointToPage(event.clientX, event.clientY);
    if (d.kind === 'move') {
      setBox({ ...d.box, cx: d.box.cx + p.x - d.startX, cy: d.box.cy + p.y - d.startY });
    } else if (d.kind === 'rotate') setBox((b) => rotateTo(b, p));
    else setBox((b) => resizeTo(b, p, limits));
  };

  const onKey = (event: KeyboardEvent<HTMLButtonElement>, kind: HandleKind): void => {
    const dir = ARROWS[event.key];
    if (!dir) return;
    event.preventDefault();
    setBox((b) => nudge(b, kind, dir, event.shiftKey, limits));
  };

  const onPageTap = (event: PointerEvent<HTMLDivElement>): void => {
    if (event.target !== event.currentTarget && !(event.target instanceof HTMLCanvasElement))
      return;
    const p = pointToPage(event.clientX, event.clientY);
    setBox((b) => ({ ...b, cx: p.x, cy: p.y }));
  };

  const handleProps = (kind: HandleKind) => ({
    onPointerDown: (e: PointerEvent<HTMLButtonElement>) => onDown(e, kind),
    onPointerMove: onMove,
    onPointerUp: () => {
      drag.current = null;
    },
    onKeyDown: (e: KeyboardEvent<HTMLButtonElement>) => onKey(e, kind),
  });
  return { handleProps, onPageTap };
}

const HANDLE_CLASS =
  'absolute flex size-11 touch-none items-center justify-center rounded-full border-2 border-ring bg-white text-ring shadow-md focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-none';

interface StageProps {
  readonly cssWidth: number;
  readonly height: number;
  readonly scale: number;
  readonly box: DisplayedBox;
  readonly image: ChosenImage;
  readonly wrapRef: RefObject<HTMLDivElement | null>;
  readonly canvasRef: RefObject<HTMLCanvasElement | null>;
  readonly handleProps: ReturnType<typeof useBoxGestures>['handleProps'];
  readonly onPageTap: (event: PointerEvent<HTMLDivElement>) => void;
}

/** The page large, with the image box over it and the three handles on the box. */
function PageStage(p: StageProps) {
  const { t } = useTranslation('tools');
  const { box, scale } = p;
  return (
    <div ref={p.wrapRef} className="flex min-w-0 flex-1 justify-center pt-12 sm:max-w-[420px]">
      <div
        className="relative touch-none bg-white shadow-lg"
        style={{ width: p.cssWidth, height: p.height }}
        onPointerDown={p.onPageTap}
      >
        <canvas ref={p.canvasRef} aria-hidden="true" className="block h-full w-full" />
        <div
          role="group"
          aria-label={t('pdf.place.group', { name: p.image.name })}
          className="absolute border-2 border-dashed border-ring"
          style={{
            left: (box.cx - box.width / 2) * scale,
            top: (box.cy - box.height / 2) * scale,
            width: box.width * scale,
            height: box.height * scale,
            transform: `rotate(${box.angle}deg)`,
          }}
        >
          <img
            src={p.image.url}
            alt=""
            draggable={false}
            className="pointer-events-none h-full w-full select-none"
          />
          <span
            aria-hidden="true"
            className="absolute -top-[18px] left-1/2 h-[18px] w-0.5 -translate-x-1/2 bg-ring"
          />
          <button
            type="button"
            aria-label={t('pdf.place.move')}
            className={`${HANDLE_CLASS} top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2`}
            {...p.handleProps('move')}
          >
            <Move aria-hidden="true" className="size-5" />
          </button>
          <button
            type="button"
            aria-label={t('pdf.place.rotate')}
            className={`${HANDLE_CLASS} -top-[62px] left-1/2 -translate-x-1/2`}
            {...p.handleProps('rotate')}
          >
            <RotateCw aria-hidden="true" className="size-5" />
          </button>
          <button
            type="button"
            aria-label={t('pdf.place.resize')}
            className={`${HANDLE_CLASS} -right-11 -bottom-11`}
            {...p.handleProps('resize')}
          >
            <Scaling aria-hidden="true" className="size-5" />
          </button>
        </div>
      </div>
    </div>
  );
}

export interface PlaceImageEditorProps {
  readonly thumbs: Promise<PdfThumbnails>;
  readonly page: number;
  /** The unrotated visible area (CropBox), in points. */
  readonly pageSize: {
    readonly x: number;
    readonly y: number;
    readonly width: number;
    readonly height: number;
  };
  /** The rotation the file gives the page, in degrees. */
  readonly ownRotation: number;
  /** Ours, added in the workspace. */
  readonly extraRotation: number;
  readonly pageNumber: number;
  readonly pageCount: number;
  readonly image: ChosenImage;
  readonly onCancel: () => void;
  readonly onPlace: (placed: PlacedImage) => void;
}

/**
 * Placing an image on one page (#387 boards, decision 99): the page large, the image with three
 * 44 px handles (move in the centre, rotate above, resize at the corner), on desktop and phone.
 * A tap on the page moves the image there. The desktop also takes exact measures in millimetres.
 * The handles are buttons: with the keyboard the arrows move, turn or resize.
 */
export function PlaceImageEditor(props: PlaceImageEditorProps) {
  const { pageSize, extraRotation, image } = props;
  const { t } = useTranslation('tools');
  const turn = normaliseTurn(props.ownRotation + extraRotation);
  const shown = displayedSize(pageSize.width, pageSize.height, turn);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const { cssWidth, measured } = usePageWidth(wrapRef);
  const ratio = image.naturalWidth / Math.max(1, image.naturalHeight);
  const [box, setBox] = useState<DisplayedBox>(() => {
    const width = shown.width * 0.3;
    return { cx: shown.width / 2, cy: shown.height / 2, width, height: width / ratio, angle: 0 };
  });
  const [keepRatio, setKeepRatio] = useState(true);

  const scale = cssWidth / shown.width; // CSS px per point
  const minPt = MIN_SHORT_SIDE_PX / scale;
  const limits: BoxLimits = { keepRatio, ratio, minPt, minWidthKept: minPt * Math.max(1, ratio) };

  // Once the page's on-screen size is known, grow a box that would be too small to handle.
  const grown = useRef(false);
  useEffect(() => {
    if (grown.current || !measured) return;
    grown.current = true;
    setBox((b) => growBox(b, scale, DEFAULT_SHORT_SIDE_PX));
  }, [scale, measured]);

  usePageRender(canvasRef, props.thumbs, props.page, cssWidth, extraRotation);
  const { handleProps, onPageTap } = useBoxGestures({ scale, canvasRef, box, setBox, limits });

  const setField = (field: Field, value: number): void => {
    if (Number.isFinite(value)) setBox((b) => applyField(b, field, value, limits));
  };
  const place = (): void => {
    props.onPlace({ bytes: image.bytes, type: image.type, ...toPdfPlacement(box, pageSize, turn) });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-[15px] font-semibold">
          {t('pdf.place.pageOf', { page: props.pageNumber, total: props.pageCount })}
        </span>
        <p className="text-sm text-muted-foreground sm:hidden">{t('pdf.place.helpTouch')}</p>
      </div>
      <div className="flex flex-col gap-6 rounded-lg bg-muted p-3 sm:flex-row sm:justify-center sm:gap-10 sm:p-6">
        <PageStage
          cssWidth={cssWidth}
          height={shown.height * scale}
          scale={scale}
          box={box}
          image={image}
          wrapRef={wrapRef}
          canvasRef={canvasRef}
          handleProps={handleProps}
          onPageTap={onPageTap}
        />
        <PlacePanel
          imageName={image.name}
          box={box}
          keepRatio={keepRatio}
          onKeepRatio={setKeepRatio}
          onField={setField}
          onCancel={props.onCancel}
          onPlace={place}
        />
      </div>
    </div>
  );
}
